/**
 * Adaptateur pour convertir les données d'invoice existantes
 * vers le format Factur-X (FacturXData)
 */

import { FacturXData, FacturXItem } from './xmlGenerator';
import { logger } from '../logger';

// Type partiel pour les données Company utilisées par Factur-X
// Accepte le type Company complet ou un objet partiel avec les champs nécessaires
interface FacturXCompanyData {
  legalName?: string;
  tradeName?: string;
  addressLine1?: string;
  zipCode?: string;
  city?: string;
  phone?: string;
  email?: string;
  registrationNumber?: string;
  vatNumber?: string;
  iban?: string;
  bic?: string;
}

/**
 * Convertit les données d'une facture existante vers le format Factur-X
 */
export function convertInvoiceToFacturX(
  invoiceData: any,
  company: FacturXCompanyData | null
): FacturXData {
  // Parser les items si c'est une string JSON
  const items = typeof invoiceData.items === 'string'
    ? JSON.parse(invoiceData.items)
    : (invoiceData.items || []);

  // Extraire le taux de TVA global depuis les données de la facture
  // Fallback à 0 si absent — les lignes individuelles portent leur propre taxPercent/taxRate
  const globalTaxRate = invoiceData.taxRate ?? invoiceData.taxPercent ?? 0;

  // Convertir les items au format Factur-X (Prisma: taxPercent, totalHT → Factur-X: taxRate, total)
  const facturxItems: FacturXItem[] = items.map((item: any, index: number) => ({
    id: String(index + 1),
    description: item.description || '',
    quantity: parseFloat(item.quantity) || 0,
    unitPrice: parseFloat(item.unitPrice) || 0,
    taxRate: parseFloat(item.taxPercent) || item.taxRate || globalTaxRate,
    total: parseFloat(item.totalHT) || parseFloat(item.total) || 0,
  }));

  // Construire les données du vendeur (société) - Utiliser le snapshot de la facture en priorité
  const seller = {
    name: invoiceData.companyName || company?.legalName || company?.tradeName || '',
    address: invoiceData.companyAddressLine1 || company?.addressLine1 || '',
    postalCode: invoiceData.companyZipCode || company?.zipCode || '',
    city: invoiceData.companyCity || company?.city || '',
    country: invoiceData.companyCountry || 'FR',
    siret: invoiceData.companySiret || company?.registrationNumber || '',  // registrationNumber = SIRET dans Prisma
    vatNumber: invoiceData.companyVatNumber || company?.vatNumber || '',
    email: invoiceData.companyEmail || company?.email,
    phone: invoiceData.companyPhone || company?.phone,
    // Coordonnées bancaires (récupérées depuis le snapshot ou company)
    iban: invoiceData.companyIban || company?.iban,
    bic: invoiceData.companyBic || company?.bic,
  };

  // Construire les données de l'acheteur (client) - Utilise UNIQUEMENT la structure Prisma V2
  const buyer = {
    name: (invoiceData as any).Client?.companyName || (invoiceData as any).Client?.fullName || '',
    address: (invoiceData as any).Client?.addressLine1 || '',
    postalCode: (invoiceData as any).Client?.zipCode || '',
    city: (invoiceData as any).Client?.city || '',
    country: (invoiceData as any).Client?.countryCode || 'FR',
    email: (invoiceData as any).Client?.email,
    phone: (invoiceData as any).Client?.phone,
    vatNumber: (invoiceData as any).Client?.vatNumber,
  };

  // Dates - Utilise issueDate (Prisma) avec fallback sur date
  const invoiceDate = invoiceData.issueDate ? new Date(invoiceData.issueDate) : (invoiceData.date ? new Date(invoiceData.date) : new Date());
  const dueDate = invoiceData.dueDate ? new Date(invoiceData.dueDate) : undefined;

  // Remise
  const discountAmount = parseFloat(invoiceData.discountAmount) || 0;
  const discountType = invoiceData.discountType || 'none';
  const discountValue = parseFloat(invoiceData.discountValue) || 0;

  let discountReason = '';
  if (discountAmount > 0) {
    if (discountType === 'percentage') {
      discountReason = `Remise ${discountValue}%`;
    } else if (discountType === 'fixed') {
      discountReason = 'Remise commerciale';
    }
  }

  // Conditions de paiement
  const paymentTerms = invoiceData.paymentTerms || 'Paiement à 30 jours fin de mois';

  // Notes et mentions légales
  const notes = invoiceData.notes;
  const legalMentions = invoiceData.legalMentions;

  return {
    invoiceNumber: invoiceData.number || invoiceData.invoiceNumber || '',
    invoiceDate,
    dueDate,
    seller,
    buyer,
    items: facturxItems,
    paymentTerms,
    legalMentions,
    notes,
    discountAmount: discountAmount > 0 ? discountAmount : undefined,
    discountReason: discountReason || undefined,
  };
}

/**
 * Valide les données minimales requises pour générer une facture Factur-X
 */
export function validateFacturXData(data: FacturXData): {
  valid: boolean;
  errors: string[];
  warnings: string[];
} {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Vérifications obligatoires
  if (!data.invoiceNumber || data.invoiceNumber.trim() === '') {
    errors.push('Numéro de facture manquant');
  }

  if (!data.invoiceDate) {
    errors.push('Date de facture manquante');
  }

  // Vendeur
  if (!data.seller.name || data.seller.name.trim() === '') {
    errors.push('Nom du vendeur manquant');
  }

  if (!data.seller.siret || data.seller.siret.trim() === '') {
    errors.push('SIRET du vendeur manquant');
  }

  if (!data.seller.vatNumber || data.seller.vatNumber.trim() === '') {
    errors.push('Numéro de TVA intracommunautaire du vendeur manquant');
  }

  if (!data.seller.address || data.seller.address.trim() === '') {
    warnings.push('Adresse du vendeur manquante');
  }

  // Acheteur
  if (!data.buyer.name || data.buyer.name.trim() === '') {
    errors.push('Nom de l\'acheteur manquant');
  }

  if (!data.buyer.address || data.buyer.address.trim() === '') {
    warnings.push('Adresse de l\'acheteur manquante');
  }

  // Items
  if (!data.items || data.items.length === 0) {
    errors.push('Aucun article dans la facture');
  }

  // Coordonnées bancaires
  if (!data.seller.iban) {
    warnings.push('IBAN du vendeur manquant');
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Affiche les erreurs et avertissements de validation
 */
export function displayValidationResults(validation: ReturnType<typeof validateFacturXData>) {
  if (validation.errors.length > 0) {
    logger.error('❌ Erreurs de validation Factur-X:');
    validation.errors.forEach(error => logger.error(`  - ${error}`));
  }

  if (validation.warnings.length > 0) {
    logger.warn('⚠️  Avertissements Factur-X:');
    validation.warnings.forEach(warning => logger.warn(`  - ${warning}`));
  }

  if (validation.valid && validation.warnings.length === 0) {
    logger.log('✅ Données Factur-X valides');
  }
}
